// DashboardClient.jsx - Version avec Tailwind CSS natif et pagination
import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import DemoViewer from '../../components/communs/DemoViewer';

import {
  Users, GraduationCap, FileQuestion, Award, Building2,
  BarChart3, TrendingUp, Clock, Calendar, CheckCircle,
  Loader2, RefreshCw, AlertCircle, BookOpen, UserCheck,
  PieChart, Activity, ChevronRight, Mail, Phone,
  User, Plus, Search, Filter, X, ClipboardList, Eye,
  Play, FolderOpen, ChevronLeft
} from 'lucide-react';

import EvaluationsClient from './EvaluationsClient';
import EvaluationRichClient from './EvaluationRichClient'; // ⭐ Import du nouveau composant

// ============================================================
// 1. COMPOSANTS STATISTIQUES (inchangés)
// ============================================================
const StatCard = ({ title, value, icon: IconComponent, color, subtitle, trend, onClick }) => (
  <div
    className={`bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition-all ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[9px] font-black text-slate-400 uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-black text-slate-800 mt-1">{value}</p>
        {subtitle && <p className="text-[9px] text-slate-400 mt-0.5">{subtitle}</p>}
        {trend !== undefined && trend !== null && (
          <span className={`text-[9px] font-bold ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-slate-400'} mt-1 inline-block`}>
            {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend || 0)}%
          </span>
        )}
      </div>
      <div className={`p-2.5 rounded-xl ${color}`}>
        {IconComponent && <IconComponent size={18} className="text-white" />}
      </div>
    </div>
  </div>
);

const ProgressBar = ({ label, value, color = 'bg-green-500', max = 100, subtitle }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-[10px]">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-slate-700">{value}%</span>
    </div>
    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all duration-500`}
        style={{ width: `${Math.min(value, max)}%` }}
      />
    </div>
    {subtitle && <p className="text-[8px] text-slate-400">{subtitle}</p>}
  </div>
);

const StatItem = ({ label, value, color, icon: IconComponent }) => (
  <div className="flex items-center justify-between p-2 bg-slate-50 rounded-xl">
    <div className="flex items-center gap-2">
      {IconComponent && <IconComponent size={14} className={color} />}
      <span className="text-xs text-slate-600">{label}</span>
    </div>
    <span className={`text-xs font-bold ${color}`}>{value}</span>
  </div>
);

// ============================================================
// 2. COMPOSANT DE LISTE DES FORMATIONS AVEC PAGINATION (inchangé)
// ============================================================
const ListeFormationsClient = ({ formations, onOpenDemo, loading, pagination, onPageChange, onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (onSearch) onSearch(val);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-green-600" size={40} />
      </div>
    );
  }

  if (formations.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
        <FolderOpen size={48} className="mx-auto text-slate-300 mb-3" />
        <p className="text-slate-500 font-medium">Aucune formation disponible</p>
        <p className="text-xs text-slate-400 mt-1">Les formations apparaîtront ici une fois qu'elles vous seront attribuées.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher une formation..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-9 pr-4 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => {
            setSearchTerm('');
            if (onSearch) onSearch('');
          }}
          className="text-xs text-slate-400 hover:text-slate-600 transition"
        >
          <X size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {formations.map((formation) => (
          <div key={formation.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all overflow-hidden">
            <div className="h-24 bg-gradient-to-r from-green-500 to-purple-600 relative">
              {formation.imageUrl && (
                <img
                  src={formation.imageUrl}
                  alt={formation.titre}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute top-2 right-2">
                <span className="text-[8px] font-bold px-2 py-0.5 bg-white/20 backdrop-blur-sm text-white rounded-full">
                  {formation.modules?.length || 0} modules
                </span>
              </div>
            </div>
            <div className="p-4">
              <h4 className="text-sm font-semibold text-slate-800 line-clamp-1">{formation.titre}</h4>
              <p className="text-[10px] text-slate-400 line-clamp-2 mt-1 min-h-[24px]">
                {formation.description || 'Aucune description'}
              </p>
              <div className="flex items-center gap-3 mt-3 text-[10px] text-slate-400">
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {formation.etudiants?.length || 0} étudiants
                </span>
                <span className="flex items-center gap-1">
                  <BookOpen size={12} />
                  {formation.modules?.length || 0} modules
                </span>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <button
                  onClick={() => onOpenDemo(formation.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg text-xs font-medium transition"
                >
                  <Eye size={14} />
                  Prévisualiser
                </button>
                <button
                  onClick={() => onOpenDemo(formation.id)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg text-xs font-medium transition"
                >
                  <Play size={14} />
                  Lire
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-xs text-slate-500">
          <span>
            Page {pagination.page + 1} / {pagination.totalPages} ({pagination.totalElements} éléments)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange && onPageChange(pagination.page - 1)}
              disabled={pagination.page === 0}
              className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-30 transition"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => onPageChange && onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages - 1}
              className="p-1.5 rounded border border-slate-200 hover:bg-slate-100 disabled:opacity-30 transition"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// 3. COMPOSANT PRINCIPAL
// ============================================================
export default function DashboardClient() {
  const { token, user, isAuthenticated } = useAuth();
  const clientId = user?.clientId || user?.client?.id || user?.id;

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  const [showFormationDemo, setShowFormationDemo] = useState(false);
  const [demoFormationId, setDemoFormationId] = useState(null);

  const [formationPagination, setFormationPagination] = useState({
    page: 0,
    size: 6,
    totalPages: 0,
    totalElements: 0
  });
  const [formationSearch, setFormationSearch] = useState('');

  const [dashboardData, setDashboardData] = useState({
    stats: {
      etudiants: { total: 0, actifs: 0, inactifs: 0 },
      formations: { total: 0, disponibles: 0, enCours: 0 },
      inscriptions: { total: 0, enAttente: 0, validees: 0, terminees: 0, rejetees: 0 },
      evaluations: { total: 0, realisees: 0, nonRealisees: 0, tauxReussite: 0, moyenne: 0 },
      evaluationsRich: { total: 0, realisees: 0, nonRealisees: 0 } // Ajout pour les stats
    },
    formations: [],
    inscriptionsRecentes: [],
    activites: [],
    progressionGlobale: 0
  });

  const fetchDashboardData = useCallback(async (page = 0, search = '') => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }
    if (!clientId) {
      setLoading(false);
      setError("Impossible de récupérer l'identifiant du client. Veuillez vous reconnecter.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Statistiques globales
      const statsUrl = `${APP_CONFIG.BASE_URL}/api/v1/client/rapport/statistiques`;
      const statsResponse = await fetch(statsUrl, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });
      let statsData = {};
      if (statsResponse.ok) {
        statsData = await statsResponse.json();
      }

      // Formations avec pagination
      const formationsUrl = `${APP_CONFIG.BASE_URL}/api/v1/client/formations?page=${page}&size=${formationPagination.size}&search=${encodeURIComponent(search)}`;
      const formationsResponse = await fetch(formationsUrl, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });
      let formationsData = [];
      let totalPages = 0;
      let totalElements = 0;
      if (formationsResponse.ok) {
        const data = await formationsResponse.json();
        formationsData = data.content || data.data || data || [];
        totalPages = data.totalPages || 0;
        totalElements = data.totalElements || formationsData.length;
      }

      // Inscriptions
      const inscriptionsResponse = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions?page=0&size=20`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Accept": "application/json"
          }
        }
      );
      let inscriptionsData = [];
      if (inscriptionsResponse.ok) {
        const data = await inscriptionsResponse.json();
        inscriptionsData = data.content || data.data || data || [];
      }

      // Évaluations classiques
      const evaluationsResponse = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations?page=0&size=50`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Accept": "application/json"
          }
        }
      );
      let evaluationsData = [];
      if (evaluationsResponse.ok) {
        const data = await evaluationsResponse.json();
        evaluationsData = data.content || data.data || data || [];
      }

      // ⭐ Évaluations enrichies (pour les stats)
      const evaluationsRichResponse = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/rich/evaluations?page=0&size=100`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Accept": "application/json"
          }
        }
      );
      let evaluationsRichData = [];
      if (evaluationsRichResponse.ok) {
        const data = await evaluationsRichResponse.json();
		// La réponse est une Page : on extrait le contenu (liste)
		if (data.content && Array.isArray(data.content)) {
		   evaluationsRichData = data.content;
		 } else if (Array.isArray(data)) {
		   evaluationsRichData = data;
		 } else {
		   evaluationsRichData = [];
		 }
      }

      // Calculs des statistiques
      const totalEtudiants = statsData.totalEtudiants || 0;
      const etudiantsActifs = statsData.etudiantsActifs || 0;
      const totalFormations = statsData.totalFormations || formationsData.length || 0;

      const totalInscriptions = statsData.totalInscriptions || inscriptionsData.length || 0;
      const inscriptionsEnAttente = inscriptionsData.filter(i =>
        i.statut === 'EN_ATTENTE' || i.statut === 'PENDING'
      ).length;
      const inscriptionsValidees = inscriptionsData.filter(i =>
        i.statut === 'VALIDEE' || i.statut === 'INSCRIT' || i.statut === 'EN_COURS'
      ).length;
      const inscriptionsTerminees = inscriptionsData.filter(i =>
        i.statut === 'TERMINE' || i.statut === 'COMPLETED'
      ).length;
      const inscriptionsRejetees = inscriptionsData.filter(i =>
        i.statut === 'REJETEE' || i.statut === 'REJECTED'
      ).length;

      const totalEvaluations = evaluationsData.length || 0;
      const evaluationsRealisees = evaluationsData.filter(e =>
        e.statut === 'TERMINE' || e.statut === 'COMPLETED'
      ).length;
      const evaluationsNonRealisees = totalEvaluations - evaluationsRealisees;

      const totalEvaluationsRich = evaluationsRichData.length || 0;
      const evaluationsRichRealisees = evaluationsRichData.filter(e =>
        e.status === 'TERMINE' || e.status === 'COMPLETED'
      ).length;
      const evaluationsRichNonRealisees = totalEvaluationsRich - evaluationsRichRealisees;

      const tauxReussite = statsData.tauxReussite || 0;
      const moyenne = statsData.noteMoyenne || 0;
      const progressionGlobale = statsData.progressionMoyenne || 0;
      const activites = statsData.activites || [];

      setDashboardData({
        stats: {
          etudiants: {
            total: totalEtudiants,
            actifs: etudiantsActifs,
            inactifs: totalEtudiants - etudiantsActifs
          },
          formations: {
            total: totalFormations,
            disponibles: statsData.totalFormationsDisponibles || 0,
            enCours: statsData.totalFormationsEnCours || 0
          },
          inscriptions: {
            total: totalInscriptions,
            enAttente: inscriptionsEnAttente,
            validees: inscriptionsValidees,
            terminees: inscriptionsTerminees,
            rejetees: inscriptionsRejetees
          },
          evaluations: {
            total: totalEvaluations,
            realisees: evaluationsRealisees,
            nonRealisees: evaluationsNonRealisees,
            tauxReussite: tauxReussite,
            moyenne: moyenne
          },
          evaluationsRich: {
            total: totalEvaluationsRich,
            realisees: evaluationsRichRealisees,
            nonRealisees: evaluationsRichNonRealisees
          }
        },
        formations: formationsData,
        inscriptionsRecentes: inscriptionsData.slice(0, 5),
        activites: activites,
        progressionGlobale: progressionGlobale
      });

      setFormationPagination(prev => ({
        ...prev,
        page: page,
        totalPages: totalPages,
        totalElements: totalElements
      }));

    } catch (err) {
      console.error("Erreur récupération dashboard:", err);
      setError(err.message || "Erreur lors du chargement des données");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, clientId, isAuthenticated, formationPagination.size]);

  useEffect(() => {
    fetchDashboardData(0, '');
  }, [fetchDashboardData]);

  const refreshData = useCallback(async () => {
    setRefreshing(true);
    await fetchDashboardData(formationPagination.page, formationSearch);
  }, [fetchDashboardData, formationPagination.page, formationSearch]);

  const handleFormationPageChange = (newPage) => {
    if (newPage >= 0 && newPage < formationPagination.totalPages) {
      fetchDashboardData(newPage, formationSearch);
    }
  };

  const handleFormationSearch = (searchTerm) => {
    setFormationSearch(searchTerm);
    fetchDashboardData(0, searchTerm);
  };

  const openFormationDemo = (formationId) => {
    setDemoFormationId(formationId);
    setShowFormationDemo(true);
  };

  if (!isAuthenticated || !token) {
    return <Navigate to="/login" replace />;
  }

  if (!clientId) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
          <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
          <p className="text-gray-700 font-medium">Erreur de configuration</p>
          <p className="text-sm text-gray-500 mt-1">Impossible de récupérer l'identifiant du client. Veuillez vous reconnecter.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
          >
            <RefreshCw size={14} /> Recharger
          </button>
        </div>
      </div>
    );
  }

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Loader2 className="animate-spin text-green-600" size={48} />
      </div>
    );
  }

  const { stats, formations, inscriptionsRecentes, activites, progressionGlobale } = dashboardData;

  return (
    <div className="space-y-4 p-4 bg-slate-50 min-h-screen font-sans">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tighter flex items-center gap-3">
            <Building2 size={28} className="text-green-600" />
            Tableau de bord Client
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Vue d'ensemble de vos étudiants, formations et évaluations
          </p>
          {user && (
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <User size={12} />
                {user.prenom} {user.nom}
              </span>
              {user.email && (
                <span className="flex items-center gap-1">
                  <Mail size={12} />
                  {user.email}
                </span>
              )}
              {user.mobile && (
                <span className="flex items-center gap-1">
                  <Phone size={12} />
                  {user.mobile}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Rafraîchissement...' : 'Rafraîchir'}
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex border-b border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden flex-wrap">
        <button
          className={`px-6 py-3 text-sm font-semibold transition-all flex-1 md:flex-none ${
            activeTab === 'dashboard'
              ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
          onClick={() => setActiveTab('dashboard')}
        >
          <span className="flex items-center justify-center gap-2">
            <BarChart3 size={16} />
            Tableau de bord
          </span>
        </button>
        <button
          className={`px-6 py-3 text-sm font-semibold transition-all flex-1 md:flex-none ${
            activeTab === 'formations'
              ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
          onClick={() => setActiveTab('formations')}
        >
          <span className="flex items-center justify-center gap-2">
            <GraduationCap size={16} />
            Mes Formations
            {formations.length > 0 && (
              <span className="ml-1 bg-green-100 text-green-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                {formationPagination.totalElements || formations.length}
              </span>
            )}
          </span>
        </button>
        <button
          className={`px-6 py-3 text-sm font-semibold transition-all flex-1 md:flex-none ${
            activeTab === 'evaluations'
              ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
          onClick={() => setActiveTab('evaluations')}
        >
          <span className="flex items-center justify-center gap-2">
            <ClipboardList size={16} />
            Évaluations
            {stats.evaluations.total > 0 && (
              <span className="ml-1 bg-purple-100 text-purple-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                {stats.evaluations.total}
              </span>
            )}
          </span>
        </button>
        {/* ⭐ Nouvel onglet pour les évaluations enrichies */}
        <button
          className={`px-6 py-3 text-sm font-semibold transition-all flex-1 md:flex-none ${
            activeTab === 'evaluations-rich'
              ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
          onClick={() => setActiveTab('evaluations-rich')}
        >
          <span className="flex items-center justify-center gap-2">
            <ClipboardList size={16} />
            Éval. enrichies
            {stats.evaluationsRich.total > 0 && (
              <span className="ml-1 bg-purple-200 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                {stats.evaluationsRich.total}
              </span>
            )}
          </span>
        </button>
      </div>

      {/* Contenu Dashboard */}
      {activeTab === 'dashboard' && (
        <>
          {/* Cartes */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Étudiants"
              value={stats.etudiants.total}
              icon={Users}
              color="bg-green-600"
              subtitle={`${stats.etudiants.actifs} actifs`}
            />
            <StatCard
              title="Formations"
              value={stats.formations.total}
              icon={GraduationCap}
              color="bg-emerald-600"
              subtitle={`${stats.formations.disponibles} disponibles`}
              onClick={() => setActiveTab('formations')}
            />
            <StatCard
              title="Inscriptions"
              value={stats.inscriptions.total}
              icon={UserCheck}
              color="bg-blue-600"
              subtitle={`${stats.inscriptions.validees} validées`}
            />
            <StatCard
              title="Évaluations"
              value={stats.evaluations.total}
              icon={FileQuestion}
              color="bg-purple-600"
              subtitle={`${stats.evaluations.realisees} réalisées`}
              onClick={() => setActiveTab('evaluations')}
            />
          </div>

          {/* Détails */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <UserCheck size={16} className="text-blue-600" />
                Inscriptions
              </h3>
              <div className="space-y-2">
                <StatItem label="En attente" value={stats.inscriptions.enAttente} color="text-amber-600" icon={Clock} />
                <StatItem label="Validées" value={stats.inscriptions.validees} color="text-green-600" icon={CheckCircle} />
                <StatItem label="Terminées" value={stats.inscriptions.terminees} color="text-blue-600" icon={Award} />
                <StatItem label="Rejetées" value={stats.inscriptions.rejetees} color="text-red-600" icon={X} />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <FileQuestion size={16} className="text-purple-600" />
                Évaluations
              </h3>
              <div className="space-y-2">
                <StatItem label="Total" value={stats.evaluations.total} color="text-purple-600" icon={FileQuestion} />
                <StatItem label="Réalisées" value={stats.evaluations.realisees} color="text-green-600" icon={CheckCircle} />
                <StatItem label="Non réalisées" value={stats.evaluations.nonRealisees} color="text-amber-600" icon={Clock} />
                <StatItem label="Taux de réussite" value={`${stats.evaluations.tauxReussite}%`} color="text-emerald-600" icon={Award} />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-emerald-600" />
                Progression
              </h3>
              <div className="space-y-3">
                <ProgressBar label="Progression globale" value={progressionGlobale} color="bg-blue-500" subtitle={`${progressionGlobale}% de la formation terminée`} />
                <ProgressBar label="Taux de réussite" value={stats.evaluations.tauxReussite} color="bg-green-500" subtitle={`${stats.evaluations.tauxReussite}% de réussite`} />
                <ProgressBar
                  label="Inscriptions validées"
                  value={stats.inscriptions.total > 0 ? Math.round((stats.inscriptions.validees / stats.inscriptions.total) * 100) : 0}
                  color="bg-amber-500"
                  subtitle={`${stats.inscriptions.validees}/${stats.inscriptions.total} validées`}
                />
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Users size={16} className="text-green-600" />
                Étudiants
              </h3>
              <div className="space-y-2">
                <StatItem label="Total" value={stats.etudiants.total} color="text-green-600" icon={Users} />
                <StatItem label="Actifs" value={stats.etudiants.actifs} color="text-green-600" icon={UserCheck} />
                <StatItem label="Inactifs" value={stats.etudiants.inactifs} color="text-slate-400" icon={User} />
                <StatItem label="Taux d'activité" value={stats.etudiants.total > 0 ? `${Math.round((stats.etudiants.actifs / stats.etudiants.total) * 100)}%` : '0%'} color="text-blue-600" icon={Activity} />
              </div>
            </div>
          </div>

          {/* Inscriptions récentes & Activités */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Calendar size={16} className="text-blue-600" />
                Inscriptions récentes
              </h3>
              {inscriptionsRecentes.length === 0 ? (
                <p className="text-xs text-slate-400 italic text-center py-4">Aucune inscription récente</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {inscriptionsRecentes.map((ins, idx) => {
                    const statusColor = {
                      'EN_ATTENTE': 'bg-amber-100 text-amber-700',
                      'PENDING': 'bg-amber-100 text-amber-700',
                      'VALIDEE': 'bg-green-100 text-green-700',
                      'INSCRIT': 'bg-green-100 text-green-700',
                      'EN_COURS': 'bg-blue-100 text-blue-700',
                      'TERMINE': 'bg-slate-100 text-slate-700',
                      'COMPLETED': 'bg-slate-100 text-slate-700',
                      'REJETEE': 'bg-red-100 text-red-700',
                      'REJECTED': 'bg-red-100 text-red-700'
                    }[ins.statut?.toUpperCase()] || 'bg-slate-100 text-slate-700';

                    const statusLabel = {
                      'EN_ATTENTE': 'En attente',
                      'PENDING': 'En attente',
                      'VALIDEE': 'Validée',
                      'INSCRIT': 'Inscrit',
                      'EN_COURS': 'En cours',
                      'TERMINE': 'Terminé',
                      'COMPLETED': 'Terminé',
                      'REJETEE': 'Rejetée',
                      'REJECTED': 'Rejetée'
                    }[ins.statut?.toUpperCase()] || ins.statut || 'N/A';

                    return (
                      <div key={ins.id || idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-xl">
                        <div className="flex items-center gap-2">
                          <User size={12} className="text-slate-400" />
                          <span className="text-xs text-slate-700">
                            {ins.etudiant?.prenom} {ins.etudiant?.nom}
                            <span className="text-[10px] text-slate-400 ml-1">→ {ins.formation?.titre}</span>
                          </span>
                        </div>
                        <span className={`text-[8px] font-bold px-2 py-0.5 rounded-full ${statusColor}`}>
                          {statusLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Activity size={16} className="text-green-600" />
                Activités récentes
              </h3>
              {activites.length === 0 ? (
                <p className="text-xs text-slate-400 italic text-center py-4">Aucune activité récente</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {activites.slice(0, 5).map((act, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{act.icon || '📌'}</span>
                        <span className="text-xs text-slate-700">{act.message}</span>
                      </div>
                      <span className="text-[9px] text-slate-400">
                        {act.date ? new Date(act.date).toLocaleDateString('fr-FR') : 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {activeTab === 'formations' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <GraduationCap size={18} className="text-emerald-600" />
              Mes Formations
              <span className="ml-2 text-xs font-normal text-slate-400">
                ({formationPagination.totalElements || formations.length} formation{formationPagination.totalElements > 1 ? 's' : ''})
              </span>
            </h3>
            <div className="text-xs text-slate-400">
              Cliquez sur "Prévisualiser" ou "Lire" pour consulter une formation
            </div>
          </div>
          <ListeFormationsClient
            formations={formations}
            onOpenDemo={openFormationDemo}
            loading={loading}
            pagination={formationPagination}
            onPageChange={handleFormationPageChange}
            onSearch={handleFormationSearch}
          />
        </div>
      )}

      {activeTab === 'evaluations' && (
        <EvaluationsClient />
      )}

      {/* ⭐ Nouvel onglet pour les évaluations enrichies */}
      {activeTab === 'evaluations-rich' && (
        <EvaluationRichClient />
      )}

      {/* Modal de démo */}
      {showFormationDemo && demoFormationId && (
        <DemoViewer
          formationId={demoFormationId}
          token={token}
          onClose={() => {
            setShowFormationDemo(false);
            setDemoFormationId(null);
          }}
          isOpen={showFormationDemo}
          userRole="client"
        />
      )}

      {/* Footer */}
      <div className="bg-slate-800 rounded-2xl p-3 text-center">
        <div className="flex items-center justify-center gap-4 text-[8px] text-slate-400 flex-wrap">
          <span className="flex items-center gap-1.5">
            <Users size={10} />
            {stats.etudiants.total} étudiants
          </span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="flex items-center gap-1.5">
            <GraduationCap size={10} />
            {stats.formations.total} formations
          </span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="flex items-center gap-1.5">
            <UserCheck size={10} />
            {stats.inscriptions.total} inscriptions
          </span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="flex items-center gap-1.5">
            <FileQuestion size={10} />
            {stats.evaluations.total} évaluations
          </span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="flex items-center gap-1.5 text-purple-400">
            <ClipboardList size={10} />
            {stats.evaluationsRich.total} éval. enrichies
          </span>
          <span className="w-px h-4 bg-slate-700" />
          <span className="text-slate-500">
            {new Date().toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    </div>
  );
}